import { paymentPurposeLabels } from "@car/api/payments/config";
import { Alert, AlertDescription, AlertTitle } from "@car/ui/components/alert";
import { Button } from "@car/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@car/ui/components/card";
import { Separator } from "@car/ui/components/separator";
import { Skeleton } from "@car/ui/components/skeleton";
import { Spinner } from "@car/ui/components/spinner";
import { Alert02Icon, BankIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { faToman } from "@/lib/format";
import { orpc } from "@/utils/orpc";

import { SEP } from "@car/ui/lib/utils";

// درگاه آزمایشی: نقش بانک را بازی می‌کند. تأیید یا رد با امضای سرور برای همین پرداخت به صفحه
// بازگشت می‌رود و آنجا تأیید نهایی سمت سرور انجام می‌شود؛ پولی جابه‌جا نمی‌شود.
export const Route = createFileRoute("/pay/test/$paymentId")({
  head: () => ({ meta: [{ title: "درگاه پرداخت آزمایشی | تابان خودرو" }] }),
  component: TestGatewayPage,
});

function TestGatewayPage() {
  const { paymentId } = Route.useParams();
  const navigate = useNavigate();
  const query = useQuery(orpc.panel.payments.get.queryOptions({ input: { paymentId } }));
  const decide = useMutation(
    orpc.panel.payments.testGateway.mutationOptions({
      onSuccess: (result) => {
        if (!result.ok || !result.params) {
          toast.error(result.reason);
          return;
        }
        navigate({
          to: "/pay/callback/$paymentId",
          params: { paymentId },
          search: result.params,
          replace: true,
        });
      },
    }),
  );
  const record = query.data;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
            <HugeiconsIcon icon={BankIcon} strokeWidth={1.8} />
          </span>
          <div>
            <CardTitle>درگاه پرداخت آزمایشی</CardTitle>
            <CardDescription>تابان خودرو{SEP}شبیه‌ساز بانک</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-5">
        <Alert variant="destructive">
          <HugeiconsIcon icon={Alert02Icon} strokeWidth={1.8} />
          <AlertTitle>این یک درگاه آزمایشی است</AlertTitle>
          <AlertDescription>
            هیچ مبلغی از کارت شما برداشت نمی‌شود. با «تأیید پرداخت» پرداخت موفق و با «رد پرداخت»
            پرداخت ناموفق شبیه‌سازی می‌شود.
          </AlertDescription>
        </Alert>

        {query.isPending ? (
          <Skeleton className="h-32 rounded-lg" />
        ) : !record ? (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            این پرداخت پیدا نشد.
          </p>
        ) : record.status !== "pending" ? (
          <div className="grid gap-3 text-center">
            <p className="text-sm text-muted-foreground">این پرداخت دیگر در انتظار درگاه نیست.</p>
            <Button
              nativeButton={false}
              render={<Link to="/pay/callback/$paymentId" params={{ paymentId }} />}
            >
              مشاهده نتیجه پرداخت
            </Button>
          </div>
        ) : (
          <>
            <dl className="grid gap-2 rounded-lg bg-muted/60 p-4 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">پذیرنده</dt>
                <dd className="font-bold">تابان خودرو</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">بابت</dt>
                <dd>{paymentPurposeLabels[record.purpose]}</dd>
              </div>
              <Separator />
              <div className="flex justify-between gap-3 text-base">
                <dt className="font-bold">مبلغ قابل پرداخت</dt>
                <dd className="font-black">{faToman(record.gatewayAmount)}</dd>
              </div>
            </dl>
            <div className="grid gap-2 sm:grid-cols-2">
              <Button
                size="lg"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ paymentId, approve: true })}
              >
                {decide.isPending && decide.variables?.approve ? (
                  <Spinner className="size-4" />
                ) : null}
                تأیید پرداخت
              </Button>
              <Button
                size="lg"
                variant="outline"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ paymentId, approve: false })}
              >
                {decide.isPending && !decide.variables?.approve ? (
                  <Spinner className="size-4" />
                ) : null}
                رد پرداخت
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
